package com.swetdot.swetgram.data.moderation

import com.swetdot.swetgram.domain.DiscoverCommentId
import com.swetdot.swetgram.domain.DiscoverPostId
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.MessageId
import com.swetdot.swetgram.domain.UserId
import kotlin.time.Instant

/*
 * Client DOMAIN models for reporting and moderation. Nothing in this file may import from
 * `com.swetdot.swetgram.protocol` (IMPLEMENTATION_NOTES §10) -- the wire types live in
 * `protocol/dto/Moderation.kt` and are mapped at the two repositories in this package.
 *
 * WHERE THIS FILE BELONGS, AND WHY IT IS NOT THERE. These are domain models, so by the rule
 * `domain/Models.kt` states they belong in `domain/` -- `domain/Profile.kt` alongside the rest of
 * the safety surface, which is exactly where `ProfileViewModel`'s old placeholder `ReportReason`
 * said it would go once a repository consumed it. `domain/` was not this change's to edit, so they
 * are here, next to their only two consumers, and they should MOVE when `domain/Profile.kt` is next
 * touched. The layering rule that matters is obeyed either way: nothing above the repositories in
 * this package sees a wire type.
 *
 *
 * THE ONE ASYMMETRY, INHERITED FROM THE CONTRACT
 *
 * `CreateReportRequest.target_type` and `.reason` are ENUMS in frozen v1.0.0; `Report.target_type`
 * and `Report.reason` are bare `type: string` with no enum. `protocol/dto/Moderation.kt` mirrors
 * that exactly, and so does this file: [ReportTarget] and [ReportReason] are closed types going
 * OUT, and [ModerationReport.targetType]/[ModerationReport.reason] are strings coming BACK, with
 * [ReportTargetKind.parse] and [ReportReason.parse] offered for the places that want to branch.
 * Tightening the response side to a Kotlin enum would turn a value a later server learned to store
 * into a decode failure on an old build, which is the opposite of what a frozen contract is for.
 */

// ---------------------------------------------------------------------------------------------
// What a report is about
// ---------------------------------------------------------------------------------------------

/**
 * The five things SPS §16.3 says a user can report, which is also exactly the contract's
 * `CreateReportRequest.target_type` enum: users, messages, Discover posts, Discover comments,
 * groups.
 *
 * There is no `UNKNOWN` member and there must not be one. This type only ever travels OUTWARD --
 * it is what the client asks the server to file -- so there is no unrecognised value to absorb, and
 * a member meaning "I do not know what this is" would be a report the server has to reject.
 * [ReportTargetKind.parse] is the inbound direction and returns null instead.
 */
enum class ReportTargetKind {
    PERSON,
    MESSAGE,
    DISCOVER_POST,
    DISCOVER_COMMENT,
    GROUP,
    PICSO_POST,
    ;

    /** The contract's spelling. `PERSON` is `user`; the rest are the obvious snake_case. */
    val wireValue: String
        get() = when (this) {
            PERSON -> "user"
            MESSAGE -> "message"
            DISCOVER_POST -> "discover_post"
            DISCOVER_COMMENT -> "discover_comment"
            GROUP -> "group"
            PICSO_POST -> "picso_post"
        }

    companion object {
        /**
         * The inbound direction, for `Report.target_type` -- which the contract leaves a bare
         * string. NULL for anything this build does not know, so an admin queue renders an
         * unfamiliar report as a row it cannot act on rather than failing to decode the page.
         */
        fun parse(raw: String): ReportTargetKind? = entries.firstOrNull { it.wireValue == raw }
    }
}

/**
 * One reportable thing, with its id.
 *
 * A sealed type rather than a `(kind, uuid)` pair, so that a caller physically cannot file a report
 * naming a [DiscoverPostId] as a `message`. The server resolves `target_id` against a DIFFERENT
 * TABLE for each `target_type` and answers one indistinguishable `404 report_target_not_found` for
 * every way that lookup can fail -- deliberately, because a bare uuid plus a type would otherwise be
 * a live existence probe for private content (ERRORS.md, Moderation). A client that crossed the two
 * would therefore get a 404 with no way to tell it from "that post is gone".
 *
 * [PERSON] is named `Person` rather than `User` because `com.swetdot.swetgram.domain.User` is
 * imported by most of the screens that build one of these, and two `User`s in one file is how the
 * wrong one gets picked.
 */
sealed interface ReportTarget {
    val kind: ReportTargetKind

    /** The bare uuid the contract's `target_id` carries. */
    val id: String

    data class Person(val userId: UserId) : ReportTarget {
        override val kind get() = ReportTargetKind.PERSON
        override val id get() = userId.value
    }

    data class Message(val messageId: MessageId) : ReportTarget {
        override val kind get() = ReportTargetKind.MESSAGE
        override val id get() = messageId.value
    }

    data class Post(val postId: DiscoverPostId) : ReportTarget {
        override val kind get() = ReportTargetKind.DISCOVER_POST
        override val id get() = postId.value
    }

    data class Comment(val commentId: DiscoverCommentId) : ReportTarget {
        override val kind get() = ReportTargetKind.DISCOVER_COMMENT
        override val id get() = commentId.value
    }

    data class Group(val groupId: GroupId) : ReportTarget {
        override val kind get() = ReportTargetKind.GROUP
        override val id get() = groupId.value
    }

    data class Picso(val postId: com.swetdot.swetgram.domain.PicsoId) : ReportTarget {
        override val kind get() = ReportTargetKind.PICSO_POST
        override val id get() = postId.value
    }
}

/**
 * SPS §16.3's seven reasons, which are the contract's `CreateReportRequest.reason` enum one for
 * one. The contract's spellings win where the prose differs: "hate or abusive content" is
 * `hate_abuse` and "illegal content" is `illegal_content`.
 *
 * No `UNKNOWN`, for [ReportTargetKind]'s reason -- this only travels outward. [parse] handles the
 * inbound side of `Report.reason`, which the contract leaves a bare string.
 */
enum class ReportReason {
    SPAM,
    SCAM,
    HARASSMENT,
    HATE_ABUSE,
    IMPERSONATION,
    ILLEGAL_CONTENT,
    OTHER,
    ;

    val wireValue: String
        get() = when (this) {
            SPAM -> "spam"
            SCAM -> "scam"
            HARASSMENT -> "harassment"
            HATE_ABUSE -> "hate_abuse"
            IMPERSONATION -> "impersonation"
            ILLEGAL_CONTENT -> "illegal_content"
            OTHER -> "other"
        }

    companion object {
        fun parse(raw: String): ReportReason? = entries.firstOrNull { it.wireValue == raw }
    }
}

/** `CreateReportRequest.details`: nullable, `maxLength: 1000`. */
const val REPORT_DETAILS_MAX: Int = 1000

// ---------------------------------------------------------------------------------------------
// What comes back
// ---------------------------------------------------------------------------------------------

/**
 * `Report.status` -- the one enum the contract DOES declare on the response side, so it is an enum
 * here.
 *
 * [UNKNOWN] exists because it is a response value and a later server may learn a sixth state; a
 * moderation queue that fails to decode is the single worst way this surface can break.
 *
 * [REVIEWING] IS REACHABLE AND NOTHING WRITES IT. No v1.0.0 operation claims a report --
 * `adminGetReport` is a GET and must not have side effects, and `adminResolveReport` goes straight
 * to a terminal state -- so today it appears only when a moderator sets it by hand with SQL. It is
 * still the one status the CLIENT can read something real from: see [ReportReceipt.alreadyInReview].
 */
enum class ReportStatus {
    OPEN,
    REVIEWING,
    RESOLVED,
    REJECTED,
    UNKNOWN,
    ;

    /** True while the report is still somebody's to decide. */
    val isOpen: Boolean get() = this == OPEN || this == REVIEWING
}

/**
 * What the reporter is handed back by `createReport`'s 201, reduced to the only three facts a
 * reporting user can be told anything with.
 *
 * DELIBERATELY NOT THE WHOLE `Report`. The wire type carries `reporter_id`, `details`,
 * `content_hash`, `assigned_to` and `resolved_at` as well; every one of those is either the
 * reporter's own input coming back or a fact about moderation staff, and none of it belongs on a
 * receipt. The admin queue reads the full row through [ModerationReport].
 */
data class ReportReceipt(
    val id: String,
    val status: ReportStatus,
    /** Null when the server's timestamp could not be parsed. Nothing here depends on it. */
    val filedAt: Instant?,
) {
    /**
     * The ONE thing a receipt can honestly say beyond "we have it", and the only inference in this
     * file.
     *
     * `createReport` answers **201 with the EXISTING report** when the reporter already has an open
     * report about the same target -- the server's `reports_open_uidx` plus `ON CONFLICT DO
     * NOTHING`, so a second report is neither filed nor refused. The 201 therefore does not say
     * whether anything was created, and the client must not guess: comparing `created_at` against
     * the phone's clock would call a fresh report a duplicate on any device whose time is a few
     * minutes fast.
     *
     * `REVIEWING` is the exception, and it is a fact rather than a guess. A newly inserted row is
     * `open` by the column's DEFAULT, so `reviewing` can ONLY have come from a report that already
     * existed and that somebody has since picked up. That is worth telling the reporter, because it
     * is the difference between "nothing has happened yet" and "a person is looking at this".
     */
    val alreadyInReview: Boolean get() = status == ReportStatus.REVIEWING
}

/**
 * A whole `Report` row, as the admin queue reads it.
 *
 * [targetType] and [reason] ARE STRINGS, mirroring the contract -- see the file header. Use
 * [targetKind] and [knownReason] to branch; both answer null for a value this build does not know,
 * and the queue renders such a row as unactionable rather than dropping it.
 *
 * WHAT IT CANNOT CARRY: the reported CONTENT. `Report` has a type and a uuid and no shape for a
 * preview, a title or an author, and no v1.0.0 operation could supply one for a post in a private
 * group a moderator has not joined. STA §16.1 asks for "view reported user/post/message metadata";
 * the contract declares nothing that serves it. Known gap -- do NOT invent a field to close it.
 */
data class ModerationReport(
    val id: String,
    val targetType: String,
    val targetId: String,
    val reason: String,
    val status: ReportStatus,
    /** Null only when the server's timestamp could not be parsed; the row is kept regardless. */
    val createdAt: Instant?,
    /** The reporter's free text, up to 1000 characters. Null when they wrote none. */
    val details: String?,
    /**
     * Lowercase hex SHA-256 of the reported text AT THE MOMENT OF FILING, or null for a `user` or
     * `group` target, which has no single piece of content.
     *
     * It is not the content and cannot be reversed into it (STA §16.3: deleted content is not
     * retained). It answers exactly two questions, both of which survive a hard delete: is the thing
     * I am about to delete the thing that was reported, and do two reports name the same text.
     */
    val contentHash: String?,
    /** Null once the reporter's account has been anonymized, per the contract's nullable field. */
    val reporterId: String?,
    /** The moderator who decided it. Null while it is open. */
    val assignedTo: String?,
    val resolvedAt: Instant?,
) {
    val targetKind: ReportTargetKind? get() = ReportTargetKind.parse(targetType)
    val knownReason: ReportReason? get() = ReportReason.parse(reason)

    /**
     * Whether this report names a single piece of CONTENT, as opposed to a whole account or group.
     *
     * The server's own rule, read off `content_hash`: it is null for `user` and `group` targets
     * precisely because there is nothing to hash. It is what gates the `delete_content` action --
     * see `AdminRepository.resolve`.
     */
    val namesContent: Boolean
        get() = targetKind == ReportTargetKind.MESSAGE ||
            targetKind == ReportTargetKind.DISCOVER_POST ||
            targetKind == ReportTargetKind.DISCOVER_COMMENT || targetKind == ReportTargetKind.PICSO_POST
}

// ---------------------------------------------------------------------------------------------
// Deciding a report
// ---------------------------------------------------------------------------------------------

/**
 * `ResolveReportRequest.decision`, required, `resolve | reject`.
 *
 * TERMINAL EITHER WAY. There is no operation in v1.0.0 that reopens a decided report, so both of
 * these are one-way doors and the UI that sends one has to say so.
 */
enum class ReportDecision {
    /** The report was right about something. */
    RESOLVE,

    /** The report was not a violation. */
    REJECT,
    ;

    val wireValue: String get() = if (this == RESOLVE) "resolve" else "reject"
}

/**
 * `ResolveReportRequest.action`, optional, `none | delete_content | block_user` -- the side effect
 * the moderator wants alongside the decision.
 *
 * The server runs it BEFORE the status write and in the same transaction, so a failed side effect
 * leaves the report undecided rather than decided-but-not-acted-on.
 *
 * ALL THREE ARE IRREVERSIBLE IN PRACTICE. `delete_content` on a Discover post is a HARD delete that
 * cascades to every comment on it; on a message it is a delete-for-everyone with a tombstone.
 * `block_user` suspends an account and revokes its sessions, and while `adminUnblockUser` exists,
 * v1.0.0 declares no operation that LISTS suspensions -- so finding the account again means knowing
 * its uuid. Whatever sends one of these owes the moderator a confirmation.
 */
enum class ReportAction {
    NONE,
    DELETE_CONTENT,
    BLOCK_USER,
    ;

    val wireValue: String
        get() = when (this) {
            NONE -> "none"
            DELETE_CONTENT -> "delete_content"
            BLOCK_USER -> "block_user"
        }
}

/** `ResolveReportRequest.note`, and the `reason` on `adminBlockUser`'s inline body. */
object ModerationText {
    /** `ResolveReportRequest.note`: nullable, `maxLength: 1000`. */
    const val NOTE_MAX: Int = 1000

    /** `adminBlockUser`'s `reason`: REQUIRED, `maxLength: 500`. */
    const val BLOCK_REASON_MAX: Int = 500
}
